import { create } from 'zustand';

// ── 공통 ──
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

// ── 채팅 ──
export interface ChatMessage {
  id: number;
  content: string;
  senderId: string;
  createdAt: string;
  chatRoomId?: number;
}

// ── 제어 큐 ──
export type ControlQueueItemStatus = 'pending' | 'success' | 'failed';

export interface ControlResult {
  commandId: number;
  nodeId: number;
  nodeName?: string;
  houseId?: number | null;
  houseName?: string | null;
  controlType: string;
  success: boolean;
  data?: Record<string, unknown> | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  timestamp?: string;
}

export interface ControlError {
  commandId?: number | null;
  nodeId?: number | null;
  errorCode: string;
  errorMessage: string;
}

export interface ControlQueueItem {
  commandId: number;
  nodeId: number;
  nodeName: string;
  controlType: string;
  status: ControlQueueItemStatus;
  result?: ControlResult;
  batchId?: string;
  registeredAt: number;
}

export interface RejectedItem {
  nodeId: number;
  nodeName: string;
  errorCode: string;
  errorMessage: string;
  batchId?: string;
}

// ── Store 타입 ──
interface RealtimeState {
  // 통합 SSE 연결 (단일 EventSource)
  sseStatus: ConnectionStatus;
  sseReconnectAttempts: number;
  sseShowError: boolean;
  sseEventSource: EventSource | null;

  // 채팅 데이터
  chatMessages: ChatMessage[];

  // 제어 큐
  controlQueue: Map<number, ControlQueueItem>;
  rejectedItems: RejectedItem[];
  panelExpanded: boolean;
}

interface RealtimeActions {
  // SSE 연결
  setSseStatus: (status: ConnectionStatus) => void;
  setSseReconnectAttempts: (n: number) => void;
  setSseShowError: (show: boolean) => void;
  setSseEventSource: (es: EventSource | null) => void;

  // 채팅
  addChatMessage: (msg: ChatMessage) => void;
  clearChatMessages: () => void;

  // 제어 큐
  addControlCommand: (item: ControlQueueItem) => void;
  addControlCommands: (items: ControlQueueItem[]) => void;
  updateCommandResult: (commandId: number, result: ControlResult) => void;
  addRejectedItems: (items: RejectedItem[]) => void;
  clearCompletedQueue: () => void;
  clearQueue: () => void;
  setPanelExpanded: (expanded: boolean) => void;

  // 리셋
  reset: () => void;
}

type RealtimeStore = RealtimeState & RealtimeActions;

const initialState: RealtimeState = {
  sseStatus: 'disconnected',
  sseReconnectAttempts: 0,
  sseShowError: false,
  sseEventSource: null,
  chatMessages: [],
  controlQueue: new Map(),
  rejectedItems: [],
  panelExpanded: false,
};

export const useRealtimeStore = create<RealtimeStore>()((set) => ({
  ...initialState,

  // ── SSE 연결 actions ──
  setSseStatus: (status) => set({ sseStatus: status }),
  setSseReconnectAttempts: (n) => set({ sseReconnectAttempts: n }),
  setSseShowError: (show) => set({ sseShowError: show }),
  setSseEventSource: (es) => set({ sseEventSource: es }),

  // ── 채팅 actions ──
  addChatMessage: (msg) => set((s) => ({ chatMessages: [...s.chatMessages, msg] })),
  clearChatMessages: () => set({ chatMessages: [] }),

  // ── 제어 큐 actions ──
  addControlCommand: (item) =>
    set((s) => {
      const next = new Map(s.controlQueue);
      next.set(item.commandId, item);
      return { controlQueue: next, panelExpanded: false };
    }),

  addControlCommands: (items) =>
    set((s) => {
      const next = new Map(s.controlQueue);
      items.forEach((item) => next.set(item.commandId, item));
      return { controlQueue: next, panelExpanded: false };
    }),

  updateCommandResult: (commandId, result) =>
    set((s) => {
      const next = new Map(s.controlQueue);
      const existing = next.get(commandId);
      if (existing) {
        next.set(commandId, {
          ...existing,
          status: result.success ? 'success' : 'failed',
          result,
        });
      }
      return { controlQueue: next };
    }),

  addRejectedItems: (items) =>
    set((s) => ({ rejectedItems: [...s.rejectedItems, ...items] })),

  clearCompletedQueue: () =>
    set((s) => {
      const next = new Map(s.controlQueue);
      next.forEach((item, key) => {
        if (item.status !== 'pending') next.delete(key);
      });
      return { controlQueue: next, rejectedItems: [] };
    }),

  clearQueue: () => set({ controlQueue: new Map(), rejectedItems: [] }),

  setPanelExpanded: (expanded) => set({ panelExpanded: expanded }),

  // ── 리셋 ──
  reset: () => set(initialState),
}));

// ── Derived selectors ──
export const selectControlQueueItems = (s: RealtimeStore) =>
  Array.from(s.controlQueue.values());

export const selectControlQueueStats = (s: RealtimeStore) => {
  const items = Array.from(s.controlQueue.values());
  const total = items.length;
  const done = items.filter((i) => i.status !== 'pending').length;
  const success = items.filter((i) => i.status === 'success').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  const pending = total - done;
  const rejectedCount = s.rejectedItems.length;
  return { total, done, success, failed, pending, rejectedCount };
};

export const selectHasActiveQueue = (s: RealtimeStore) =>
  s.controlQueue.size > 0;
